<template lang="html">
    <div class="simulations">
        <top-nav></top-nav>
        <page-title 
            :title="getText('simulations_title')" 
            icon="reports" 
            simulationIndicator 
        ></page-title>
        <div class="actions-bar">
            <div class="container">
                <el-row :gutter="24">
                    <el-col :span="8">
                        <div class="main-action-wrapper">
                            <el-button 
                                class="greenButton" 
                                @click="triggerNewSimulation()"
                                :disabled="selectedFactory?false:true"
                            >
                                {{getText('new_simulation_button')}}
                            </el-button>
                        </div>
                    </el-col>
                    <el-col :offset="8" :span="8">
                        <el-input 
                            :placeholder="getText('simulations_search_bar')" 
                            class="researchField" 
                            v-model="filter"
                            :disabled="selectedFactory?false:true"
                        >
                        </el-input>
                    </el-col>
                </el-row>
            </div>
        </div>
        <div
            v-if="$route.params.uid"
            class="save-data-btn"
        >
            <el-dialog
              class="dialog"
              :visible.sync="dialogUploadCsvVisible"
              :title="getText('new_simulation_dialog_data_title')"
              :close-on-click-modal="closeOnClickModal"
              :close-on-press-escape="closeOnPressEscape"
              center ref="newSimulationDialog"
            >
                <el-alert
                    v-if="disabled"
                    class="warning-message"
                    :title="getText('new_simulation_warning_message')"
                    type="info"
                    :closable="false"
                    center
                >
                </el-alert>
                <el-input class="simulationName" :placeholder="getText('new_simulation_dialog_placeholder')" type="text" v-model="newSimulationName" ></el-input>
                <el-upload
                    drag
                    ref="upload"
                    class="upload-demo"
                    accept=".csv"
                    action=""
                    :before-upload="handleChange"
                    required
                >
                    <i class="el-icon-upload"></i>
                    <div class="el-upload__text">{{getText('new_simulation_dialog_data_text')}}</div>
                    <div class="el-upload__tip" slot="tip">{{getText('new_simulation_dialog_data_info')}}</div>
                </el-upload>
                <div ref="graph" id="graph"></div>
                <div v-if="plotting_in_progress">
                    <p class="waiting">{{getText('simulation_waiting_time')}}</p>
                </div>
                <div v-if="uploadedFile && !plotting_in_progress" class="rangeSelector">
                    <span class="label">{{getText('simulation_chart_audit_range_label')}}</span>
                    <el-date-picker
                        v-model="range"
                        @change="changeGraphRange"
                        type="datetimerange"
                        :start-placeholder="getText('simulation_chart_audit_start_date')"
                        :end-placeholder="getText('simulation_chart_audit_end_date')"
                        :range-separator="getText('simulation_chart_audit_range_separator')"
                        class="datetime"
                    >
                    </el-date-picker>
                </div>

                <div
                    v-if="caliberIsNeeded"
                    class="caliber"
                >
                    <p class="caliber-title">
                        {{getText('new_simulation_dialog_caliber')}}
                    </p>
                    <el-select v-model="simulationCaliber" placeholder="">
                        <el-option
                            v-for="value in [20, 50, 100, 200, 1000]"
                            :key="value"
                            :label="value"
                            :value="value"
                        >
                        </el-option>
                    </el-select>
                </div>

                <div class="dialogButtons">
                    <el-button class="whiteButton" :disabled="loadings.save" @click="closeDialog">{{getText('cancel_button')}}</el-button>
                    <!-- Disable (Hide) the tooltip when the button isn't disabled anymore -->
                    <el-tooltip popper-class="simulationCreationTooltip" :visible-arrow="false" :disabled="!disabled" :content="getContentForTooltip" placement="top" effect="dark">
                        <div>
                            <el-button v-loading="loadings.save" class="greenButton" @click="saveRange" :disabled="disabled || loadings.save">{{getText('save_button')}}</el-button>
                        </div>
                    </el-tooltip>
                </div>
            </el-dialog>
        </div>
        <el-dialog class="dialog" :visible.sync="newDialog" :title="getText('new_simulation_dialog_title')" center>
            <div class="wrapper">
                <transition name="no-mode-translate-fade" class="transition" mode="out-in">
                    <div v-if="newDialogStep === 0" key="0" class="button-wrapper">
                        <p class="title">
                            {{getText('new_simulation_dialog_step_one')}}
                        </p>
                        <div
                            class="factories"
                            v-for="(factory, index) in sortedFactories"
                            :key="index"
                        >
                            <el-button @click="setSiteUid(factory.uid)" class="greenButton">{{factory.name}}</el-button>
                        </div>
                    </div>
                    <div v-if="newDialogStep === 1" key="1" style="width: 100%">
                        <div v-if="getFactoryEngines(site_uid).length != 0" class="button-wrapper">
                            <p class="title">
                                {{getText('new_simulation_dialog_step_two')}}
                            </p>
                            <div
                                class="motors"
                                v-for="(motor, index) in getFactoryEngines(site_uid)"
                                :key="index"
                            >
                                <el-button @click="setMotorUid(motor.uid)" class="greenButton">{{motor.name}}</el-button>
                            </div>
                        </div>
                        <div v-else style="width: 100%" class="button-wrapper">
                            <p class="title block">
                                {{getText('new_simulation_dialog_step_two_empty')}}
                            </p>
                            <router-link :to="{ name: 'moteur', params: { siteid: site_uid } }" style="margin: auto;">
                                <el-button class="greenButton">{{getText('new_simulation_dialog_step_two_empty_button')}}</el-button>
                            </router-link>
                        </div>
                    </div>
                </transition>
            </div>
        </el-dialog>
        <el-dialog
            :title="getText('simulation_archiving_title')"
            :visible.sync="archivingSimulation"
            width="30%"
            class="archivingDialog"
            :key="simulationToArchive.uid"
            @close="closeArchiveModal">
            <p>{{getText('simulation_archiving_warning')}}</p>
            <span slot="footer">
                <el-button
                    class="whiteButton"
                    @click="closeArchiveModal"
                >{{getText('cancel_button')}}</el-button>
                <el-button
                    class="greenButton"
                    type="primary"
                    @click="archive(simulationToArchive)"
                >{{getText('simulation_archive_button')}}</el-button>
            </span>
        </el-dialog>
        <el-dialog
            :visible.sync="errorModalIsOpened"
            width="70%"
            class="error-modal"
        >
            <div slot="title">
                <p class="modal-title"><i class="el-icon-error error-icon"></i>{{getText('simulation_error_modal_title')}}</p>
            </div>
            <div>
                <el-row
                    :gutter="20"
                >
                    <el-col :span="8">
                        <p v-if="simError.short_description" class="error-title">{{simError.short_description[getLangage]}}</p>
                    </el-col>
                    <el-col :span="16">
                        <vue-markdown v-if="simError.long_description" class="alert-desc" :source="simError.long_description[getLangage]"></vue-markdown>
                    </el-col>
                </el-row>
            </div>
            <span slot="footer">
                <el-button @click="errorModalIsOpened = false">{{getText('close_button')}}</el-button>
            </span>
        </el-dialog>
        <el-dialog
            :visible.sync="alertModalIsOpened"
            width="70%"
            class="alert-modal"
        >
            <div slot="title">
                <p class="modal-title"><i class="el-icon-warning warning-icon"></i>{{ simAlerts.length }} {{ simAlerts.length > 1 ? getText('simulation_alert_modal_title_multiple') : getText('simulation_alert_modal_title_single') }}</p>
            </div>
            <div>
                <el-row
                    v-for="(alert, index) in simAlerts"
                    :key="index"
                    :gutter="20"
                >
                    <el-col :span="8">
                        <p class="alert-title">{{alert.short_description[getLangage]}}</p>
                    </el-col>
                    <el-col :span="16">
                        <vue-markdown class="alert-desc" :source="alert.long_description[getLangage]"></vue-markdown>
                    </el-col>
                </el-row>
            </div>
            <span slot="footer">
                <el-button @click="alertModalIsOpened = false">{{getText('close_button')}}</el-button>
            </span>
        </el-dialog>
        <div class="viveco-footer-background">
            <div class="container">
                <ns-paginate
                    v-if="selectedFactory"
                    ref="nsPaginate"
                    baseUrl='simulation/'
                    :displayCsvButton="false"
                    :customItemPageLength="6"
                    :customFiltersToApply="customFiltersToApply"
                >
                    <div slot="filter" class="filter__container">
                        <p class="audit-title"> Audit sélectionné :</p>
                        <div class="align-input-description">
                            <el-select
                                :value="selectedFactory"
                                placeholder="Select"
                                value-key="uid"
                                filterable
                                @change="updateSelectedFactory"
                                :disabled="selectedFactory?false:true"
                            >
                                <el-option
                                    v-for="item in sortedFactories"
                                    :key="item.uid"
                                    :label="item.name"
                                    :value="item">
                                </el-option>
                            </el-select>
                            <span class="selected-description">
                                Par défaut, les simulations affichées sont liées au dernier audit sélectionné. Pour choisir un <br> autre audit, cliquer sur la flèche et sélectionnez l'audit désiré dans la liste.
                            </span>
                        </div>
                    </div>
                    <div slot="reload"></div>
                    <div
                        slot="list"
                        slot-scope="props"
                    >
                        <el-row :gutter="24" class="simulations-container">
                            <div 
                                v-if="props.list.length === 0"
                                class="no-simulation-text"
                            >
                                {{getText('no_simulation_found')}}
                            </div>
                            <el-col
                                v-else
                                v-for="sim in props.list"
                                class="card"
                                :key="sim.uid"
                                :md="8"
                                :sm="12"
                                style="margin-bottom: 40px;"
                            >
                                <el-card class="simulation-card card-1-cta card-engine" :class="{'simOfUser': sim.user_uid === getCurrentUserUid}">

                                    <el-button class="archive-btn" v-if="sim.status === 'FAILED' || sim.status ==='COMPLETED'" size="mini" icon="el-icon-delete" type="info" @click="toggleSimulationArchivingModal(sim)" circle>
                                    </el-button>

                                    <div slot="header">
                                        <i v-if="sim.alerts_data.length > 0 || sim.warning === true" class="el-icon-warning simulation-warning"></i>
                                        <i v-else-if="Object.keys(sim.error_data).length > 0" class="el-icon-error simulation-error"></i>
                                        <span>{{sim.name}}</span>
                                    </div>
                                    <div>
                                        {{sim.creation_date | moment('DD/MM/YYYY HH:mm')}}
                                    </div>
                                    <div>
                                        <div class="info">
                                            <p v-if="sim.status === 'RUNNING'">{{getText('simulations_status_computing')}}</p>
                                            <p v-else-if="sim.status === 'PENDING'">{{getText('simulations_status_pending')}}</p>
                                            <router-link v-else-if="sim.status === 'COMPLETED'" :to="{ name: 'simulation', params: { uid: sim.uid } }">
                                                <el-button class="greenButton cardButton notCancelled">{{getText('simulation_check_button')}}</el-button>
                                            </router-link>
                                            <el-button v-if="Object.keys(sim.error_data).length > 0" type="danger" icon="ti-alert" class="cardButton modalButton error-modal-button" style="margin-top: 40px !important" @click="openErrorModal(sim)">{{getText('simulation_error_modal_opening_button')}}</el-button>
                                            <div v-else-if="sim.status === 'FAILED' && Object.keys(sim.error_data).length === 0" style="margin-top: 40px; color: #f56c6c">
                                                {{getText('simulation_error_server')}}
                                            </div>
                                            <el-button v-else-if="sim.alerts_data.length" icon="el-icon-warning-outline" type="warning" class="cardButton modalButton" @click="openAlertsModal(sim)">{{getText('simulation_alert_modal_opening_button')}}</el-button>
                                        </div>
                                    </div>
                                </el-card>
                            </el-col>
                        </el-row>
                    </div>
                </ns-paginate>
                <div
                    v-else
                >
                    <div class="no-factory-text">
                        {{getText('no_factory')}}
                    </div>
                </div>
            </div>
        </div>

        <page-footer></page-footer>
    </div>
</template>

<script>
import moment from 'moment';
import {text, csvParseRows, dsvFormat} from 'd3';
import topNav from '../ui/top-nav.vue';
import pageTitle from '../ui/page-title.vue';
import pageFooter from '../ui/page-footer.vue';
import Plotly from 'plotly.js-basic-dist';
import {mapGetters} from 'vuex';
import nsPaginate from 'fundation-app-vuejs/src/components/partials/nsPaginate'
import VueMarkdown from 'vue-markdown'

const MEASURE_FILE_VIVECO_MANUEL_FORMAT = 'viveco-manuel';
const MEASURE_FILE_MOTORBOX_V2_FORMAT = 'motorbox-v2';
const MEASURE_FILE_MOTORBOX_FLUKE_EN_FORMAT = 'motorbox-fluke-en';
const MEASURE_FILE_MOTORBOX_FLUKE_FR_FORMAT = 'motorbox-fluke-fr';

const MEASURE_FILE_MOTORBOX_V2_FORMAT_EXCEL_MODIFIED = 'motorbox-v2-excel-modified';

const LITERAL_MONTHS_MAP = {
  // TODO check the format
    'janv.': '1',
    'févr.': '2',
    'mars': '3',
    'avr.': '4',
    'mai': '5',
    'juin': '6',
    'juil.': '7',
    'août': '8',
    'sept.': '9',
    'oct.': '10',
    'nov.': '11',
    'déc.': '12',
}

var MEASURE_FILE_SUPPORTED_FORMATS = {
    [MEASURE_FILE_VIVECO_MANUEL_FORMAT]: {
        offset: 0,
        matchPattern: 'ANNEE;MOIS;JOUR;HEURE;MIN;SEC;Moyenne(V);',
        parsePattern: [
            new RegExp('^(?<year>\\d{4});(?<month>\\d+);(?<day>\\d+);(?<hour>\\d+);(?<minute>\\d+);(?<second>\\d+);(?<value>[\\d.,]+);')
        ],
    },
    // [MEASURE_FILE_MOTORBOX_V2_FORMAT_EXCEL_MODIFIED]: {
    //     offset: 5,
    //     matchPattern: 'Date;Current;Power;TempC;TempB;VBat1;VBat2;VBat3;DACA;DACB;Vraw;VrawCorr;DACoutA;Cal.;;;;;',
    //     parsePattern: new RegExp('^(?<day>\\d+)/(?<month>\\d+)/(?<year>\\d{4}) (?<hour>\\d+):(?<minute>\\d+);(?<value>[\\d,.]+);'),
    // },
    [MEASURE_FILE_MOTORBOX_V2_FORMAT]: {
        offset: 5,
        matchPattern: 'Date;Current;Power;TempC;TempB;VBat1;VBat2;VBat3;DACA;DACB;Vraw;VrawCorr;DACoutA;Cal.',
        parsePattern: [
            new RegExp('^(?<day>\\d+)/(?<month>\\d+)/(?<year>\\d{4}) (?<hour>\\d+):(?<minute>\\d+);(?<value>[\\d,.]+);'),
            new RegExp('^(?<hour>\\d+):(?<minute>\\d+):(?<second>\\d+)-(?<day>\\d+)/(?<month>\\d+)/(?<year>\\d{2});(?<value>[\\d,.]+);'),
        ],
    },

    [MEASURE_FILE_MOTORBOX_FLUKE_EN_FORMAT]: {
        offset: 7,
        matchPattern: 'Reading,L\'heure de démarrage,Duration,Max Time,maximal (A AC),Moy (A AC)',
        parsePattern: [
            // 1,2023-11-06 11:35:09,0:01:00,2023-11-06 11:35:12,58.3,57.9,2023-11-06 11:35:12,57.6,2023-11-06 11:36:09
            new RegExp('^(?<idx>\\d+),(?<year>\\d{4})\\-(?<month>\\d{2})\\-(?<day>\\d{2})\\s(?<hour>\\d+):(?<minute>\\d+):(?<second>\\d+),(?<duration>\\d+:\\d+:\\d+),(?<maxTime>\\d{4}\\-\\d{2}-\\d{2}\\s\\d{2}:\\d{2}:\\d{2}),(?<maxAc>[\\d.]+),(?<value>[\\d.]+),')
        ],
    },
    [MEASURE_FILE_MOTORBOX_FLUKE_FR_FORMAT]: {
        offset: 7,
        matchPattern: 'Relevé,Heure de démarrage,Durée,Temps maximal,Max(A AC),Moyenne(A AC)',
        parsePattern: [
            // 1,10 mai 2023 15:25:57,00:00:10,10 mai 2023 15:26:05,"51.4","51.3",10 mai 2023 15:25:57,"50.8",10 mai 2023 15:26:07
            new RegExp('^(?<idx>\\d+),(?<day>\\d+)\\s(?<month>[\\wéû\\.]+)\\s(?<year>\\d{4})\\s(?<hour>\\d+):(?<minute>\\d+):(?<second>\\d+),(?<duration>\\d+:\\d+:\\d+),(?<maxTime>\\d+\\s[\\wéû\\.]+\\s\\d{4}\\s\\d{2}:\\d{2}:\\d{2}),(?<maxAc>[\\d.]+),(?<value>[\\d.]+)')
        ],
    }
}

export default {
    components: {
        VueMarkdown,
        topNav,
        pageTitle,
        pageFooter,
        nsPaginate
    },
    data() {
        return {
            simError: {},
            simAlerts: [],
            alertModalIsOpened: false,
            errorModalIsOpened: false,
            range: [],
            defaultRange: [],
            loadings: {
                save: false,
                restore: false
            },
            // displayGrid: false,
            page: 0,
            pageSize: 6,
            filter: '',

            site_uid: '',
            motor_uid: '',

            plotting_in_progress: false,
            dialogUploadCsvVisible: true,
            newDialog: false,
            newDialogStep: 0,

            uploadedFile: null,

            newSimulationName: '',
            simulationFileFormat: 'unknow',
            simulationCaliber: '',
            simulationToArchive: {},
            archivingSimulation: false,
            closeOnClickModal: true,
            closeOnPressEscape: true,
        };
    },
    computed: {
        ...mapGetters([
            'getText',
            'factories',
            'getFactoryEngines',
            'simulations',
            'currentUser',
            'getLangage',
            'factoriesByUid',
        ]),
        customFiltersToApply() {
            return () => {
                return {
                    archived: false,
                    factory_uid: this.factoryUid,
                }
            }
        },
        caliberIsNeeded(){
            return this.simulationFileFormat === MEASURE_FILE_VIVECO_MANUEL_FORMAT;
        },
        caliberIsMissing(){
            return (this.caliberIsNeeded && this.simulationCaliber === '');
        },
        getContentForTooltip() {
            if (!this.newSimulationName) {
                return this.getText('new_simulation_name_error');
            }
            if (this.uploadedFile === null) {
                return this.getText('new_simulation_file_error');
            }
            if (this.caliberIsMissing) {
                return this.getText('new_simulation_caliber_error');
            }
        },
        getCurrentUserUid() {
            return this.currentUser.uid || '';
        },
        filterValueLower(){
            return this.filter.toLowerCase();
        },
        disabled() {
            return (!this.newSimulationName || !this.uploadedFile || this.caliberIsMissing);
        },
        factoryUid (){
            if(this.$store.getters.selectedFactory){
                return this.$store.getters.selectedFactory
            }

            if(this.factories){
                return this.factories[0].uid
            }

            return '';
        },
        sortedFactories(){
            let sortedFactories = JSON.parse(JSON.stringify(this.factories))
            return sortedFactories.sort((a,b) => a.name > b.name?0:-1)
        },
        selectedFactory(){
            if(!this.factories){
                return null
            }
            if(this.$store.getters.selectedFactory === null){
                return this.factories[0]
            }
            return this.factoriesByUid(this.$store.getters.selectedFactory)
        }
    },
    watch: {
        filter(val) {
            this.$refs.nsPaginate.filtersValue.searchFilter = val;
            this.$refs.nsPaginate.loadContent();
        }
    },
    methods: {
        updateSelectedFactory(value){
            this.$store.dispatch('storeSelectedFactory', value).then(()=>{
               this.$refs.nsPaginate.loadContent()
            })
        },
        openErrorModal(sim) {
            this.$set(this, 'simError', sim.error_data);
            this.errorModalIsOpened = true;
        },
        openAlertsModal(sim) {
            this.$set(this, 'simAlerts', sim.alerts_data);
            this.alertModalIsOpened = true;
        },
        closeArchiveModal() {
            this.archivingSimulation = false;
        },
        changeGraphRange(rangeArray) {
            if (rangeArray) {
                this.$set(this, 'range', rangeArray);
                Plotly.relayout('graph', 'xaxis.range', [ moment(rangeArray[0]).format('YYYY-MM-DDTHH:mm:ssZ'), moment(rangeArray[1]).format('YYYY-MM-DDTHH:mm:ssZ') ]);
            }
            else {
                Plotly.relayout('graph', 'xaxis.autorange', true);

            }
        },
        toggleSimulationArchivingModal(sim) {
            this.$set(this, 'simulationToArchive', sim);
            this.archivingSimulation = true;
        },
        archive(sim) {
            this.$store.axios.patch(
                `${window.config.api_root_url}/simulation/${sim.uid}/`,
                {archived: true},
            ).then(response => {
                this.$notify.success({
                    title: this.getText('notify_success_title'),
                    message: this.getText('notify_simulation_archive_successful')
                });
                this.$refs.nsPaginate.loadContent();
            }).catch(error => {
                this.$notify.error({
                    title: this.getText('notify_failed_title'),
                });
            }).finally(() => {
                this.closeArchiveModal();
            });
        },
        closeDialog() {
            this.dialogUploadCsvVisible = false;
            if (this.$route.params.uid){
                this.$route.params.uid = null;
            }
        },
        changePage(nb) {
            this.page = nb - 1;
        },
        setSiteUid(uid) {
            this.site_uid = uid;
            this.newDialogStep++;
        },
        setMotorUid(uid) {
            this.motor_uid = uid;
            this.newDialogStep++;
            this.newDialog = false;
            this.dialogUploadCsvVisible = true;
            this.$route.params.uid = this.motor_uid;
        },
        triggerNewSimulation() {
            let plotDiv = document.getElementById('graph');
            if(plotDiv){
                Plotly.purge(plotDiv);
            }

            this.newDialog = true;
            this.site_uid = '';
            this.newSimulationName = '';
            this.motor_uid = '';
            this.newDialogStep = 0;
            this.simulationCaliber = '';
            if (this.$refs.upload){
                this.$refs.upload.clearFiles();
            }
            this.plotting_in_progress = false;
            this.uploadedFile = null;
        },
        async getTextFromFile (file){
            return new Promise((resolve, reject) => {
                let reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.onabort = reject;
                reader.onerror = reject;
                reader.readAsText(file);
            })
        },
        getMeasureFileFormat(fileContentLines){
            // Info there :
            // https://git.netsach.info/edf/viveco/support/-/wikis/format-fichier-mesure
            for (const formatName in MEASURE_FILE_SUPPORTED_FORMATS) {
              let format = MEASURE_FILE_SUPPORTED_FORMATS[formatName];
              if(fileContentLines[format.offset] && fileContentLines[format.offset].startsWith(format.matchPattern)){
                return formatName;
              }
            }
            // Unsupported format
            throw new Error('Unsupported measure file format');
        },
        handleChange(file) {
            this.loadFileAndDrawGraph(file);
            // Prevent file uploading
            return false;
        },
        async loadFileAndDrawGraph(file){

            this.plotting_in_progress = true;
            this.uploadedFile = file;

            try{
                var fileContent = await this.getTextFromFile(file);
                // Clean file, remove BOM : <0xfeff>, 0xFEFF, uFEFF
                fileContent = fileContent.replace(/^\uFEFF/gm, '').replace(/^\u00BB\u00BF/gm,"").replace(/"/gm,"");
                let fileContentLines = fileContent.split(/\r?\n/);
                this.simulationFileFormat = this.getMeasureFileFormat(fileContentLines);

                let measureFileFormat = MEASURE_FILE_SUPPORTED_FORMATS[this.simulationFileFormat];

                // Use `splice` method here to avoid `slice` shallow copy
                fileContentLines.splice(0, measureFileFormat.offset + 1);
                let choosenPattern = null;
                let measures = fileContentLines.flatMap(line => {
                    if(line){
                        if (!choosenPattern){
                            // If the pattern is not set, try all the possible patterns
                            // on the first line, and choose the first that matches
                            measureFileFormat.parsePattern.forEach(pattern => {
                                let matches = line.match(pattern);
                                if(matches){
                                    choosenPattern = pattern;
                                }
                            })
                            if (!choosenPattern) {
                                console.error(`No match for following line ${line}`);
                                throw new Error('Unsupported measure file format');
                            }
                        }
                        let matches = line.match(choosenPattern);
                        if(matches){
                            return [matches.groups];
                        }
                        else{
                            console.error('No match for following line');
                            throw new Error('Unsupported measure file format');
                        }
                    }
                    return [];
                });

                let x = [];
                let y = [];
                // console.debug(measures)
                for (let measure of measures) {
                    let month = measure.month;
                    if (measure.month in LITERAL_MONTHS_MAP){
                        month = LITERAL_MONTHS_MAP[measure.month]
                    }
                    x.push(`${measure.year}-${month.padStart(2, '0')}-${measure.day.padStart(2, '0')}T${measure.hour.padStart(2, '0')}:${measure.minute.padStart(2, '0')}`);
                    y.push(measure.value);
                }
                this.makePlotly(x, y);
            }
            catch(e){
                console.error(e);
                this.$notify.error({
                    message: this.getText('notify_simulation_data_error')
                });
            }
        },

        makePlotly(x, y) {
            let traces = [{
                x: x,
                y: y
            }];
            // Use annotations because yaxis.title cannot be rotated
            let layout = {
                annotations: [{
                    xref: 'paper',
                    yref: 'paper',
                    x: 0,
                    xanchor: 'right',
                    y: 1,
                    yanchor: 'bottom',
                    text: '(A)',
                    showarrow: false
                }]
            };
            if (this.caliberIsNeeded) {
                layout.annotations[0].text = "(mV)";
            }
            Plotly.newPlot('graph', traces, layout).then(data => {
                this.$set(this, 'defaultRange', [data.layout.xaxis.range[0], data.layout.xaxis.range[1]]);
                this.$set(this, 'range', this.defaultRange);
            });
            this.plotting_in_progress = false;
            this.$refs.graph.on('plotly_relayout', (data) => {
                if (data['xaxis.range[0]'] && data['xaxis.range[1]']) { // when changes are thrown through the chart
                    this.$set(this, 'range', [data['xaxis.range[0]'], data['xaxis.range[1]']]);
                }
                else if (data['xaxis.range'] && data['xaxis.range'].length === 2) { // when changes are thrown through the datetime picker
                    this.$set(this, 'range', [data['xaxis.range'][0], data['xaxis.range'][1]]);
                }
                else { // when resetting to zoomed out value, or error
                    this.$set(this, 'range', this.defaultRange);
                }
            });
        },
        saveRange() {
            this.closeOnPressEscape = false;
            this.closeOnClickModal = false;
            let messageBox = this.$message({
                message: this.getText('global_user_information_latency'),
                closable: false,
                duration: 0,
                customClass: 'new-simulation-user-information',
                iconClass: ''
            });
            this.loadings.save = true;
            let formData = new FormData();
            formData.append('model_name', 'simulation');
            formData.append('status', 'PENDING');
            formData.append('extra_file', this.uploadedFile);

            let context = {
                'user_uid': this.getCurrentUserUid,
                'motor_uid': this.$route.params.uid != '' ? this.$route.params.uid : this.motor_uid,
                'name': this.newSimulationName,
                'start_date': this.range[0],
                'end_date': this.range[1],
                'caliber': this.simulationCaliber
            };
            formData.append('context', JSON.stringify(context));

            this.$store.axios.post(
                `${window.config.api_root_url}/creation-request/`,
                formData,
                {headers: {'Content-Type': 'multipart/form-data'}}
            ).then((response) => {
                return this.$store.dispatch('awaitCreationRequest', response.data.url);
            }).then(() => {
                this.$notify({
                    type: 'success',
                    title: this.getText('notify_success_title'),
                    message: this.getText('notify_simulation_successful')
                });
            }).catch(() => {
                this.$notify({
                    type: 'error',
                    title: this.getText('notify_failed_title')
                });
            }).finally(() => {
                this.$refs.nsPaginate.loadContent();
                messageBox.close();
                this.loadings.save = false;
                this.dialogUploadCsvVisible = false;
                this.$route.params.uid = '';
                this.closeOnPressEscape = true;
                this.closeOnClickModal = true;

            });
        },
    }
};
</script>

<style lang="less">
@import '../../less/variables';
.alert-modal, .error-modal {
    font-size: 24px;
    word-break: break-word;
    .modal-title {
        font-size: 30px;
        text-align: center;
        .error-icon {
            color: #F56C6C;
            margin-right: 20px;
        }
        .warning-icon {
            color: #E6A23C;
            margin-right: 20px;
        }
    }
    .error-title, .alert-title {
        text-align: left;
        font-weight: bold
    }
    .error-desc, .alert-desc {
        text-align: left;
    }
}
.new-simulation-user-information {
//messageBox class, that element is appended to the body not to the component
    background-color: #F4F4F5;
    color: #909399;
    border: none;
}
.simulationCreationTooltip {
    background: @color-1 !important;
    font-size: 13px;
}
.wrapper {
    // position: relative;
    display: flex;
    width: 100%;
    flex-wrap: nowrap;
    height: auto;
    .button-wrapper {
        width: 100%;
        // position: absolute;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;
        .title {
            width : 100%;
            margin-bottom: 40px;
        }
    }
    .no-mode-translate-fade-enter-active, .no-mode-translate-fade-leave-active {
        transition: all 0.5s;
    }
    .no-mode-translate-fade-enter, .no-mode-translate-fade-leave-active {
        opacity: 0;
    }
    .no-mode-translate-fade-enter {
        transform: translateX(31px);
    }
    .no-mode-translate-fade-leave-active {
        transform: translateX(-31px);
    }
    .factories, .motors {
        margin: 0 auto 30px;
    }
    // .tmp {
    //     margin-top: 150px;
    // }
}

.upload-demo .el-upload input[type="file"]{
    display: none;
}

.simulations {
    .viveco-footer-background {
        .container {
            margin: auto auto;
            .simulations-container {
                min-height: 560px;
            }
            .card {
                height: 240px;
                max-height: 240px;
                .el-card__header {
                    div {
                        display: flex;
                        align-items: baseline;
                        .simulation-warning {
                            color: #e6a23c;
                            font-size: 14px;
                            margin-right: 5px;
                            opacity: 0.8;
                        }
                        .simulation-error {
                            color: #f56c6c;
                            font-size: 14px;
                            margin-right: 5px;
                            opacity: 0.8;
                        }
                    }
                }
                .simOfUser {
                    border: solid 2px #c4d601;
                }
                .simulation-card {
                    min-height: 250px;
                    height: 250px;
                }
            }
        }
    }
    .actions-bar {
        padding-bottom: 40px;
    }
    .rangeSelector {
        text-align: left;
        margin-left: 5%;
        .label {
            margin-right: 30px;
        }
        .datetime {
            margin-bottom: 20px;
        }
    }
    .caliber {
        text-align: left;
        margin-left: 5%;
        margin-bottom: 20px;
        .caliber-title {
            margin-bottom: 0;
        }
        .el-select {
            margin-top: 0;
        }
    }
    .archivingDialog {
        .el-dialog__title {
            margin-left: 0;
        }
    }
    .el-pagination {
        text-align: center !important;
        button, ul li {
            background-color: transparent !important;
        }
        button:hover, ul li:hover {
            color: @color-1 !important;
        }
        .el-pagination__sizes {
            display: none !important;
        }
    }

}
.greenButton {
    border: none;
    text-transform: uppercase;
    font-family: worksans, Arial, "Helvetica Neue", Helvetica, sans-serif;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 1px 5px 0px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 3px 1px -2px;
    background: @color-1;
    color: white !important;
    &.is-disabled {
        background: @color-1;
        opacity: 0.5;
        &:hover {
            background: @color-1;
            opacity: 0.5;
        }
    }
}
.researchField {
    margin-top: 20px;
    input {
        border-top: none;
        border-left: none;
        border-right: none;
        border-radius: 0;
        transition-duration: 0.4s;
        &:focus {
            border-bottom-color: @color-1 !important;
            border-bottom-width: 2px !important;
        }
    }
}

.cardButton {
    margin: auto !important;
    width: 100%;
    font-size: 14px;
    text-transform: uppercase;
    &.modalButton {
        margin-top: 10px !important;
    }
    &.error-modal-button {
        i {
            margin-right: 5px;
        }
    }
}
.notCancelled {
    margin-top: 40px !important;
}

.dialog {
    .simulationName {
        input {
            border-top: none;
            border-right: none;
            border-left: none;
            border-radius: 0;
            border-color: @color-1;
            text-align: center;
        }
        width: 60%;
        margin: 0 auto 40px;
    }
    .warning-message {
        margin-bottom: 10px;
        background-color: #f7fbcf;
    }
}

.upload-demo {
    .el-upload:focus .el-upload-dragger {
        border-color: @color-1 !important;
        color: @color-1 !important;
    }
}

.researchField {
    margin-top: 20px;
    input {
        border-top: none;
        border-left: none;
        border-right: none;
        border-radius: 0;
        transition-duration: 0.4s;
        &:focus {
            border-bottom-color: @color-1 !important;
            border-bottom-width: 2px !important;
        }
    }
}
.number {
    margin: 0 !important;
}

.el-pager li.active {
    color: @color-1;
}
.el-dialog__body {
    text-align: center !important;
    .el-upload__tip {
        margin-bottom: 20px;
    }
    .el-upload-dragger:hover {
        border-color: @color-1;
    }
    .icon {
        margin-top: 55px;
        .icon-minus {
            font-size: 27px;
            color: @color-1;
        }
        .icon-plus {
            font-size: 27px;
            color: @color-1;
        }
    }
    .spaceTop {
        margin-top: 40px;
    }
}

.el-upload-list__item.is-success .el-upload-list__item-name:focus, .el-upload-list__item.is-success .el-upload-list__item-name:hover {
    color: @color-1;
}

.info {
    text-align: center;
    p {
        margin: 30px 0 20px;

    }
}

.waiting {
    text-align: center;
    width: 60%;
    margin: 40px auto;
    color: @color-1;
    font-size: 15px;
}

#graph {
    .plot{
        path {
            stroke: @color-1 !important;
        }
    }
    .hovertext {
        path {
            fill: @color-1 !important;
        }
    }
}
.filter__container{
    margin-bottom: 1.5rem;
}
.no-simulation-text{
    margin-left: 12px;
    font-size: 30px;
}
.no-factory-text {
    margin-top: 3rem;
    text-align: center;
    font-size: 30px;
}
.selected-description{
    color: grey;
    font-style: italic;
    float: right;
    font-size: 12px;
    margin-left: 1.5em;
}
.align-input-description {
    display: flex
}
.audit-title{
    font-weight: bold;
    color: grey;
    margin-top: 1em;
}
</style>



// WEBPACK FOOTER //
// src/components/page/simulations.vue